// BotMessage.js
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react"; // Added useCallback
import PropTypes from "prop-types";
import { Button, Tooltip, Spinner, CardImg } from "reactstrap"; // Added Spinner
import { Play, Pause, BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import libraryService from "../../../services/contentLibraryService"; // Assuming this can fetch by ID/title
import ChatAssessmentField from "./ChatAssessmentField";

import "./styles.scss";
import Avatar from "../../../components/Avatar/Avatar";
import { sanitizeHTML } from "../../../utils/utils";

const BotMessage = ({
  content,
  audioData,
  audioMimeType,
  textStreamComplete,
  citations: initialCitationIdentifiers,
  urls = [], // New prop for URLs
  className = "",
  messageId,
  onCitationClick,
  avatarSrc = null,
  autoPlayAudio, // Make sure this prop is received
  isHistorical = false, // Flag to indicate if message is from history
  question = null, // Question object for ChatAssessmentField
  onAnswerChange = null, // Callback for answer changes
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const [audioTipOpen, setAudioTipOpen] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false); // Track if we've already auto-played

  const [showCitationsList, setShowCitationsList] = useState(false);
  const [sourcesButtonTipOpen, setSourcesButtonTipOpen] = useState(false);
  const toggleSourcesButtonTip = () =>
    setSourcesButtonTipOpen(!sourcesButtonTipOpen);

  // State for fetched/enriched citations
  const [enrichedCitations, setEnrichedCitations] = useState([]);
  const [citationsLoading, setCitationsLoading] = useState(false);
  const [citationsError, setCitationsError] = useState(null);

  const baseId = useMemo(
    () => messageId || Math.random().toString(36).substring(7),
    [messageId]
  );
  const audioButtonId = `audio-btn-${baseId}`;
  const sourcesButtonId = `sources-btn-${baseId}`;

  const toggleAudioTip = () => setAudioTipOpen(!audioTipOpen);

  // Audio useEffects (no change)
  useEffect(() => {
    /* ... unmount cleanup ... */ return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        if (audioRef.current.src && audioRef.current.src.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }
      }
    };
  }, []);
  useEffect(() => {
    /* ... audioData change ... */ if (audioRef.current && audioData) {
      if (audioRef.current.src && audioRef.current.src.startsWith("blob:")) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current.pause();
      audioRef.current = null;
      if (autoPlayAudio) {
        setIsPlaying(true);
        audioRef.current = new Audio(URL.createObjectURL(audioData));
        audioRef.current.play();
      } else {
        setIsPlaying(false);
      }
    }
  }, [audioData]);

  // Fetch enriched citation data when initial identifiers are available and text is complete
  const fetchEnrichedCitations = useCallback(async () => {
    if (
      !initialCitationIdentifiers ||
      initialCitationIdentifiers.length === 0 ||
      !textStreamComplete
    ) {
      setEnrichedCitations([]); // Clear if no identifiers or text not complete
      return;
    }

    // Prevent re-fetching if already loaded for this set of identifiers (optional optimization)
    // This simple check assumes identifiers array order and content doesn't change frequently for the same message.
    // A more robust check might involve comparing a hash or deep equality if needed.
    if (
      enrichedCitations.length === initialCitationIdentifiers.length &&
      enrichedCitations.every((ec) =>
        initialCitationIdentifiers.includes(ec.lookupTitle)
      )
    ) {
      // console.log("Citations seem to be already loaded for these identifiers.");
      // return;
    }

    setCitationsLoading(true);
    setCitationsError(null);
    try {
      const fetchedCitations = await Promise.all(
        initialCitationIdentifiers.map(async (identifier) => {
          try {
            // 'loadArticle' is used here; ensure it can take the 'identifier' (current title string)
            // and returns an object that includes at least 'id' and 'title' for display,
            // and preferably the full article object if onCitationClick needs it.
            const article = await libraryService.loadArticle(identifier);
            return {
              lookupTitle: identifier, // The original identifier used for lookup
              id: article.id, // Actual ID from the fetched article
              displayTitle: article.title, // Actual title from the fetched article
              articleData: article, // The full article object for the popup
            };
          } catch (error) {
            console.warn(
              `Failed to fetch details for citation: ${identifier}`,
              error
            );
            return {
              lookupTitle: identifier,
              id: identifier, // Fallback ID
              displayTitle: `${identifier} (details not found)`, // Fallback display
              error: true,
              articleData: { title: identifier }, // Minimal fallback for popup title
            };
          }
        })
      );
      setEnrichedCitations(fetchedCitations);
    } catch (error) {
      console.error("Error fetching enriched citations batch:", error);
      setCitationsError("Could not load some citation details.");
      // Optionally set enrichedCitations to a state reflecting partial failure
      setEnrichedCitations(
        initialCitationIdentifiers.map((id) => ({
          lookupTitle: id,
          id,
          displayTitle: `${id} (error)`,
        }))
      );
    } finally {
      setCitationsLoading(false);
    }
  }, [initialCitationIdentifiers, textStreamComplete]); // Removed enrichedCitations from deps to avoid loop with simple check

  useEffect(() => {
    fetchEnrichedCitations();
  }, [fetchEnrichedCitations]); // fetchEnrichedCitations is memoized

  // Update the audioData useEffect to handle autoplay
  useEffect(() => {
    if (audioData && textStreamComplete && !hasAutoPlayed) {
      // Clean up previous audio if exists
      if (audioRef.current) {
        if (audioRef.current.src && audioRef.current.src.startsWith("blob:")) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Create new audio and handle autoplay
      try {
        const byteCharacters = atob(audioData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const audioBlob = new Blob([byteArray], {
          type: audioMimeType || "audio/mpeg",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        const newAudio = new Audio(audioUrl);

        audioRef.current = newAudio;

        // Set up event handlers
        newAudio.onended = () => setIsPlaying(false);
        newAudio.onerror = (e) => {
          setIsPlaying(false);
          console.error("Audio playback error:", e);
          URL.revokeObjectURL(audioUrl);
        };

        // Handle autoplay - only for non-historical messages
        if (autoPlayAudio && !isHistorical) {
          newAudio
            .play()
            .then(() => {
              setIsPlaying(true);
              setHasAutoPlayed(true);
            })
            .catch((e) => {
              console.error("Error auto-playing audio:", e);
              setIsPlaying(false);
              URL.revokeObjectURL(audioUrl);
            });
        } else {
          setIsPlaying(false);
        }
      } catch (error) {
        console.error("Error processing audio data:", error);
        setIsPlaying(false);
      }
    }
  }, [
    audioData,
    textStreamComplete,
    autoPlayAudio,
    hasAutoPlayed,
    isHistorical,
  ]);

  // Audio play/pause handler (no change)
  const handlePlayPause = () => {
    /* ... */ if (!audioData) return;
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else if (audioRef.current && !isPlaying) {
      audioRef.current.play().catch((e) => {
        console.error("Error resuming audio:", e);
        setIsPlaying(false);
      });
      setIsPlaying(true);
    } else {
      try {
        const byteCharacters = atob(audioData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const audioBlob = new Blob([byteArray], {
          type: audioMimeType || "audio/mpeg",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        const newAudio = new Audio(audioUrl);
        audioRef.current = newAudio;
        newAudio
          .play()
          .then(() => setIsPlaying(true))
          .catch((e) => {
            console.error("Error playing audio:", e);
            setIsPlaying(false);
            URL.revokeObjectURL(audioUrl);
          });
        newAudio.onended = () => setIsPlaying(false);
        newAudio.onerror = (e) => {
          setIsPlaying(false);
          console.error("Audio playback error:", e);
          URL.revokeObjectURL(audioUrl);
        };
      } catch (error) {
        console.error("Error processing or playing audio data:", error);
        setIsPlaying(false);
      }
    }
  };

  // Now passes the full enriched citation object (or just its lookupTitle if preferred for popup re-fetch)
  const handleInternalCitationClick = (citationObject, e) => {
    e.preventDefault();
    e.stopPropagation();
    // Pass the original lookup title, or the actual article ID, or the full articleData
    // Depending on what CitationInfoPopup expects. Let's assume it still takes the lookupTitle
    // or the fetched article's actual title/ID for its own fetching/display.
    // If CitationInfoPopup can take the full articleData directly, that's even better.
    if (onCitationClick && citationObject) {
      onCitationClick(citationObject.lookupTitle);
    }
  };

  const toggleShowCitationsList = (e) => {
    e.stopPropagation();
    setShowCitationsList(!showCitationsList);
  };

  return (
    <div
      className={`bot-message-container ${className}`}
      style={{
        margin: "24px 0",
        display: "flex",
        gap: "16px",
        animation: "fadeInMessage 0.3s ease",
      }}
    >
      {/* Avatar - AI style */}
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "13px",
          fontWeight: "600",
          flexShrink: 0,
          background: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)", // Softer purple gradient
          color: "white",
        }}
      >
        {avatarSrc ? (
          <Avatar name="Bot" photo={avatarSrc} size={32} rounded />
        ) : (
          "AI"
        )}
      </div>

      {/* Message Content - Clean with subtle border */}
      <div
        style={{
          flex: 1,
          lineHeight: "1.6",
          backgroundColor: "#ffffff",
          padding: "14px 18px",
          borderRadius: "12px",
          border: "1px solid #e2e8f0", // Very light border
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)", // Very subtle shadow for depth
        }}
      >
        {content && (
          <div
            className="bot-message-text"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}
            style={{
              margin: 0,
              fontSize: "15px",
              color: "#1e293b", // Darker text for better contrast
            }}
          />
        )}

        {/* Render ChatAssessmentField if question is provided */}
        {question && textStreamComplete && (
          <div style={{ marginTop: "12px" }}>
            <ChatAssessmentField
              question={question}
              onAnswerChange={onAnswerChange}
            />
          </div>
        )}
        {(textStreamComplete && audioData) ||
        (textStreamComplete &&
          ((initialCitationIdentifiers &&
            initialCitationIdentifiers.length > 0) ||
            (urls && urls.length > 0))) ? (
          <div className="bot-message-actions-bar d-flex align-items-center mt-2 pt-2 border-top">
            {textStreamComplete && audioData && (
              <div className="bot-action-item me-2">
                <Button
                  id={audioButtonId}
                  color="link"
                  size="sm"
                  onClick={handlePlayPause}
                  className={`bot-action-button p-1 ${
                    isPlaying ? "playing" : ""
                  }`}
                  aria-label={isPlaying ? "Pause audio" : "Play audio"}
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </Button>
                <Tooltip
                  placement="top"
                  isOpen={audioTipOpen}
                  target={audioButtonId}
                  toggle={toggleAudioTip}
                >
                  {isPlaying ? "Pause" : "Listen"}
                </Tooltip>
              </div>
            )}

            {textStreamComplete &&
              ((initialCitationIdentifiers &&
                initialCitationIdentifiers.length > 0) ||
                (urls && urls.length > 0)) && (
                <div className="bot-action-item">
                  <Button
                    id={sourcesButtonId}
                    color="link"
                    size="sm"
                    onClick={toggleShowCitationsList}
                    className="bot-action-button p-1 d-flex align-items-center"
                    disabled={citationsLoading}
                  >
                    <BookOpen size={16} className="me-1" />
                    <span style={{ fontSize: "0.85rem" }}>Sources</span>
                    {citationsLoading ? (
                      <Spinner size="sm" className="ms-1" />
                    ) : showCitationsList ? (
                      <ChevronUp size={16} className="ms-1" />
                    ) : (
                      <ChevronDown size={16} className="ms-1" />
                    )}
                  </Button>
                  {!citationsLoading && (
                    <Tooltip
                      placement="top"
                      isOpen={sourcesButtonTipOpen}
                      target={sourcesButtonId}
                      toggle={toggleSourcesButtonTip}
                    >
                      {showCitationsList ? "Hide sources" : "View sources"}
                    </Tooltip>
                  )}
                </div>
              )}
          </div>
        ) : null}
        {showCitationsList && textStreamComplete && (
          <div className="bot-citations-list-wrapper mt-2">
            {citationsLoading && !enrichedCitations.length && (
              <div className="text-center py-2">
                <Spinner size="sm" /> Loading sources...
              </div>
            )}
            {citationsError && (
              <p
                className="text-danger text-center py-1"
                style={{ fontSize: "0.8rem" }}
              >
                {citationsError}
              </p>
            )}
            {!citationsLoading &&
              (enrichedCitations.length > 0 || (urls && urls.length > 0)) && (
                <ul className="bot-citations-list">
                  {enrichedCitations.map((citation, index) => (
                    <li
                      key={`${messageId}-enriched-citation-${
                        citation.id || index
                      }`}
                      className="citation-item"
                    >
                      <a
                        href="#"
                        onClick={(e) =>
                          handleInternalCitationClick(citation, e)
                        }
                        className={`citation-link ${
                          citation.error ? "text-danger" : ""
                        }`}
                        title={
                          citation.error
                            ? `Could not load: ${citation.lookupTitle}`
                            : citation.displayTitle
                        }
                      >
                        {citation.displayTitle || citation.lookupTitle}
                      </a>
                    </li>
                  ))}
                  {urls &&
                    urls.map((url, index) => (
                      <li
                        key={`${messageId}-url-${index}`}
                        className="citation-item"
                      >
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="citation-link"
                          title={url}
                        >
                          {url}
                        </a>
                      </li>
                    ))}
                </ul>
              )}
            {!citationsLoading &&
              !citationsError &&
              !enrichedCitations.length &&
              (!urls || !urls.length) &&
              initialCitationIdentifiers &&
              initialCitationIdentifiers.length > 0 && (
                <p
                  className="text-muted text-center py-1"
                  style={{ fontSize: "0.8rem" }}
                >
                  No sources found.
                </p>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

BotMessage.propTypes = {
  content: PropTypes.string,
  audioData: PropTypes.string, // Base64 encoded audio data
  audioMimeType: PropTypes.string,
  textStreamComplete: PropTypes.bool.isRequired,
  citations: PropTypes.arrayOf(PropTypes.string),
  urls: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  messageId: PropTypes.string.isRequired,
  onCitationClick: PropTypes.func,
  avatarSrc: PropTypes.string,
  autoPlayAudio: PropTypes.bool,
  isHistorical: PropTypes.bool,
  question: PropTypes.object,
  onAnswerChange: PropTypes.func,
};

BotMessage.defaultProps = {
  content: "",
  audioData: null,
  audioMimeType: "audio/mpeg",
  citations: [],
  urls: [],
  className: "",
  onCitationClick: null,
  avatarSrc: null,
  autoPlayAudio: false,
  isHistorical: false,
  question: null,
  onAnswerChange: null,
};

export default BotMessage;
